import { z } from 'zod'
import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  honeybookReviewListDtoSchema,
  honeybookSignInStartDtoSchema,
  venueHoneybookDtoSchema,
  venueHoneybookStatusDtoSchema,
  type HoneybookReviewListDto,
  type HoneybookSignInStartDto,
  type VenueHoneybookDto,
  type VenueHoneybookStatusDto,
} from '@lib/dto/honeybook.dto'
import type { ConfirmHoneybookReviewInput, FinishHoneybookSignInInput, StartHoneybookSwitchInput, UpdateHoneybookConnectionInput } from '@lib/input/honeybook.input'
import type { IHoneyBookService } from '@/types/HoneyBookService.types'

/** A venue's HoneyBook connection (docs/honeybook.spec.md, "Connecting"). */
class HoneyBookServiceImpl implements IHoneyBookService {
  async venue(venueId: string): Promise<VenueHoneybookDto> {
    try {
      return await api.get(`/venues/${venueId}/honeybook`, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s HoneyBook connection', { venueId }, error)
      throw error
    }
  }

  async status(venueId: string): Promise<VenueHoneybookStatusDto> {
    try {
      return await api.get(`/venues/${venueId}/honeybook/status`, venueHoneybookStatusDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to check whether the venue’s pipeline has HoneyBook', { venueId }, error)
      throw error
    }
  }

  async startSignIn(venueId: string): Promise<HoneybookSignInStartDto> {
    try {
      return await api.post(`/venues/${venueId}/honeybook/sign-in`, {}, honeybookSignInStartDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a HoneyBook sign-in', { venueId }, error)
      throw error
    }
  }

  async finishSignIn(input: FinishHoneybookSignInInput): Promise<VenueHoneybookDto> {
    try {
      return await api.post('/honeybook/connections', input, venueHoneybookDtoSchema)
    } catch (error) {
      // Never the code or the state: they are one-time credentials.
      logRequestFailure('Failed to finish a HoneyBook sign-in', {}, error)
      throw error
    }
  }

  async refresh(venueId: string): Promise<VenueHoneybookDto> {
    try {
      return await api.post(`/venues/${venueId}/honeybook/refresh`, {}, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to read the venue’s HoneyBook', { venueId }, error)
      throw error
    }
  }

  async saveSettings(venueId: string, input: UpdateHoneybookConnectionInput): Promise<VenueHoneybookDto> {
    try {
      return await api.put(`/venues/${venueId}/honeybook`, input, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save what the venue’s HoneyBook stages mean', { venueId }, error)
      throw error
    }
  }

  async disconnect(venueId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/honeybook`)
    } catch (error) {
      logRequestFailure('Failed to disconnect HoneyBook', { venueId }, error)
      throw error
    }
  }

  async forget(venueId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/honeybook/history`)
    } catch (error) {
      logRequestFailure('Failed to forget the old HoneyBook connection', { venueId }, error)
      throw error
    }
  }

  async review(venueId: string): Promise<HoneybookReviewListDto> {
    try {
      return await api.get(`/venues/${venueId}/honeybook/review`, honeybookReviewListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s events waiting to be matched to HoneyBook', { venueId }, error)
      throw error
    }
  }

  async confirmReview(venueId: string, itemId: string, input: ConfirmHoneybookReviewInput): Promise<void> {
    try {
      await api.post(`/venues/${venueId}/honeybook/review/${encodeURIComponent(itemId)}/confirm`, input, z.null())
    } catch (error) {
      logRequestFailure('Failed to hand an event over to its HoneyBook booking', { venueId, itemId }, error)
      throw error
    }
  }

  async rejectReview(venueId: string, itemId: string): Promise<void> {
    try {
      await api.post(`/venues/${venueId}/honeybook/review/${encodeURIComponent(itemId)}/reject`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to keep an event the venue’s own', { venueId, itemId }, error)
      throw error
    }
  }

  async matchAgain(venueId: string): Promise<VenueHoneybookDto> {
    try {
      return await api.post(`/venues/${venueId}/honeybook/match`, {}, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to match the venue’s events to HoneyBook again', { venueId }, error)
      throw error
    }
  }

  async startSwitch(venueId: string, input: StartHoneybookSwitchInput): Promise<VenueHoneybookDto> {
    try {
      return await api.post(`/venues/${venueId}/honeybook/switch`, input, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set the venue’s switch from HoneyBook', { venueId }, error)
      throw error
    }
  }

  async cancelSwitch(venueId: string): Promise<VenueHoneybookDto> {
    try {
      return await api.delFor(`/venues/${venueId}/honeybook/switch`, venueHoneybookDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to call off the venue’s switch from HoneyBook', { venueId }, error)
      throw error
    }
  }
}

export const honeybookService: IHoneyBookService = new HoneyBookServiceImpl()
